import { MediaKind, ParsedMessage, ReceivedMessage, ReplyToData } from "../types";

// ─── Local Storage ────────────────────────────────────────────────────────────

export const saveMessagesToLocalStorage = (msgs: ParsedMessage[]): void => {
  localStorage.setItem("chatMessages", JSON.stringify(msgs));
};

// ─── Timestamp ────────────────────────────────────────────────────────────────

export const formatTimestamp = (timestamp: number): string => {
  return new Date(timestamp).toLocaleTimeString([], {
    hour: "2-digit",
    minute: "2-digit",
    hour12: false,
  });
};

// ─── Parse Reply Data ─────────────────────────────────────────────────────────

export const parseReplyData = (
  parsedData: any,
  userDokter: string,
): ReplyToData | null => {
  if (!parsedData?.user_reply_id) return null;

  return {
    messageID: parsedData.message_id_reply,
    senderUserID: parsedData.user_reply_id,
    message: parsedData.value_reply,
    type_reply: parsedData.type_reply || "text",
    isDoctor: parsedData.user_reply_id === userDokter,
  };
};

// ─── Parse JSON safely ────────────────────────────────────────────────────────

export const safeJsonParse = (str: string): any | null => {
  try {
    return JSON.parse(str);
  } catch {
    return null;
  }
};

// ─── Filter system messages ───────────────────────────────────────────────────
// 'end|' sengaja tidak difilter di UI karena dipakai untuk trigger
// consultation:ended event, bukan ditampilkan sebagai bubble chat.

const SYSTEM_MESSAGE_SUBSTRINGS = [
  "rejected|",
  "cancelled|",
  "rescheduled|",
  "completed|",
  "expired|",
];

export const isSystemMessage = (message: string): boolean => {
  return SYSTEM_MESSAGE_SUBSTRINGS.some((substr) => message.includes(substr));
};

// ─── Build message from raw ZIM message ──────────────────────────────────────

export const buildParsedMessage = (
  msg: ReceivedMessage,
  userDokter: string,
  currentUserId: string,
): ParsedMessage => {
  const timestamp = formatTimestamp(msg.timestamp);
  const isMedia = msg.type !== 1;
  const parsedData = safeJsonParse(msg.message);
  const replyTo = parseReplyData(parsedData, userDokter);
  const isOwn = msg.senderUserID === currentUserId;
  const mediaKind = resolveMediaKind(msg.type, msg.fileName, msg.largeImageDownloadUrl || msg.fileDownloadUrl || msg.thumbnailDownloadUrl);

  if (isMedia) {
    return {
      messageID: msg.messageID,
      message: msg.message || "[media]",
      type: "media",
      timestamp,
      rawTimestamp: msg.timestamp,
      isDoctor: msg.senderUserID === userDokter,
      imageUrl:
        msg.largeImageDownloadUrl ||
        msg.fileDownloadUrl ||
        msg.thumbnailDownloadUrl,
      fileName: msg.fileName || "Unknown file",
      mediaKind,
      replyTo,
      isOwn,
      syncStatus: "synced",
    };
  }

  return {
    messageID: msg.messageID,
    message: msg.message,
    type: "text",
    timestamp,
    rawTimestamp: msg.timestamp,
    isDoctor: msg.senderUserID === userDokter,
    replyTo,
    isOwn,
  };
};

export interface BackendChatMessage {
  id?: string | number;
  message_id?: string;
  messageID?: string;
  message?: string;
  content?: string;
  created_at?: string;
  timestamp?: number;
  time?: number | string;
  type_message?: number | string;
  from_userId?: string;
  sender_id?: string;
  senderUserID?: string;
  file_url?: string;
  file_name?: string;
  original_name?: string;
  size?: number;
  mime_type?: string;
}

export const resolveMediaKind = (
  typeMessage?: number | string,
  fileName?: string,
  fileUrl?: string,
): MediaKind => {
  const numericType = Number(typeMessage);
  if (numericType === 11) return "image";
  if (numericType === 13) return "audio";
  if (numericType === 14) return "video";

  const target = `${fileName || ""} ${fileUrl || ""}`.toLowerCase();
  if (/\.(jpg|jpeg|png|gif|webp|bmp)/.test(target)) return "image";
  if (/\.(mp3|wav|ogg|m4a|aac)/.test(target)) return "audio";
  if (/\.(mp4|mov|avi|mkv|webm)/.test(target)) return "video";

  return "file";
};

export const parseBackendChatMessage = (
  item: BackendChatMessage,
  userDokter: string,
): ParsedMessage | null => {
  const messageID =
    item.message_id ||
    item.messageID ||
    item.id ||
    "";
  if (!messageID) return null;

  const rawMessage = item.message || item.content || "";
  const senderID =
    item.sender_id || item.from_userId || item.senderUserID || "";
  const timestamp =
    item.timestamp ||
    (item.created_at ? new Date(item.created_at).getTime() : 0) ||
    (item.time ? Number(item.time) : 0);
  const typeMessage = Number(item.type_message || 1);
  const isMedia = typeMessage !== 1;
  const parsedData = typeof rawMessage === "string" ? safeJsonParse(rawMessage) : null;
  const replyToData =
    parsedData && typeof parsedData === "object"
      ? parseReplyData(parsedData, userDokter)
      : null;
  const fileUrl = item.file_url || parsedData?.url;
  const fileName =
    item.original_name ||
    item.file_name ||
    parsedData?.original_name ||
    parsedData?.file_name;

  return {
    messageID: String(messageID),
    message: rawMessage,
    timestamp: timestamp ? formatTimestamp(timestamp) : "",
    rawTimestamp: timestamp,
    isDoctor: senderID === userDokter,
    type: isMedia ? "media" : "text",
    replyTo: replyToData,
    imageUrl: isMedia ? fileUrl : undefined,
    fileName: isMedia ? fileName : undefined,
    fileSize: isMedia ? item.size || parsedData?.size : undefined,
    mediaKind: isMedia ? resolveMediaKind(typeMessage, fileName, fileUrl) : undefined,
    syncStatus: isMedia ? "synced" : undefined,
  };
};
